import { useEffect, useMemo, useState } from "react";
import { Layout } from "lucide-react";
import { EmptyState } from "./ui/empty-state";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "./ui/resizable";
import { WidgetsSidebar } from "./widgets/WidgetsSidebar";
import { WidgetPreview } from "./widgets/WidgetPreview";
import { useAppWorkspace } from "@/contexts/AppWorkspaceContext";
import { useQuery } from "convex/react";
import { api } from "@/convex/src/_generated/api";
import { syncResources } from "@/lib/mcp-sync-api";
import { fetchAccessToken } from "@/hooks/use-session";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { RefreshCw } from "lucide-react";
import type { MCPServerConfig } from "@/sdk";
import type { Id } from "@/convex/src/_generated/dataModel";

interface WidgetsTabProps {
  serverConfig?: MCPServerConfig;
  serverName?: string;
}

interface Widget {
  _id: Id<"canonicalWidgets">;
  workspaceId: Id<"appWorkspaces">;
  appWorkspaceServerId: Id<"appWorkspaceServers">;
  slug: string;
  displayName: string;
  description?: string;
  widgetKind?: string;
  canonicalResourceId: Id<"canonicalResources">;
  uiBundleMetadata?: any;
  createdAt: number;
  updatedAt: number;
}

export function WidgetsTab({ serverConfig, serverName }: WidgetsTabProps) {
  // fetchAccessToken is imported from use-session
  const { activeWorkspace } = useAppWorkspace();
  const [selectedWidgetId, setSelectedWidgetId] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState("");
  const [syncing, setSyncing] = useState(false);

  // Query servers to get the appWorkspaceServerId from serverName
  const servers = useQuery(
    api.appWorkspaceServers.listServers,
    activeWorkspace?._id ? { workspaceId: activeWorkspace._id } : "skip",
  );

  // Find the current server by name to get its ID
  const currentServer = useMemo(() => {
    if (!servers || !serverName) return null;
    return servers.find((s) => s.name === serverName) ?? null;
  }, [servers, serverName]);

  // Query canonical widgets for the current workspace + server
  const widgets = useQuery(
    api.canonicalEntities.listCanonicalWidgetsForServer,
    activeWorkspace?._id && currentServer?._id
      ? {
          workspaceId: activeWorkspace._id,
          appWorkspaceServerId: currentServer._id,
        }
      : "skip",
  ) as Widget[] | undefined;

  const widgetsList = widgets ?? [];
  const fetchingWidgets = widgets === undefined;

  // Filter widgets by search query
  const filteredWidgets = useMemo(() => {
    if (!searchQuery.trim()) return widgetsList;
    const query = searchQuery.trim().toLowerCase();
    return widgetsList.filter((widget) => {
      const haystack =
        `${widget.displayName} ${widget.slug} ${widget.description ?? ""}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [widgetsList, searchQuery]);

  // Selected widget data
  const selectedWidget = useMemo(() => {
    return widgetsList.find((w) => w._id === selectedWidgetId) ?? null;
  }, [widgetsList, selectedWidgetId]);

  // Clear selection when server changes
  useEffect(() => {
    setSelectedWidgetId("");
  }, [serverName]);

  const handleSync = async () => {
    if (!serverName) return;

    setSyncing(true);
    try {
      const token = await fetchAccessToken();
      await syncResources(serverName, token);
      toast.success("Widgets synced successfully");
      // Convex query will auto-refresh due to reactivity
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast.error(`Sync failed: ${message}`);
    } finally {
      setSyncing(false);
    }
  };

  if (!serverConfig) {
    return (
      <EmptyState
        icon={Layout}
        title="No Server Selected"
        description="Connect to an MCP server to explore its available widgets."
      />
    );
  }

  return (
    <div className="h-full flex flex-col">
      <ResizablePanelGroup direction="vertical" className="flex-1">
        <ResizablePanel defaultSize={70} minSize={30}>
          <ResizablePanelGroup direction="horizontal" className="h-full">
            <WidgetsSidebar
              widgets={widgetsList}
              filteredWidgets={filteredWidgets}
              selectedWidgetId={selectedWidgetId}
              fetchingWidgets={fetchingWidgets}
              searchQuery={searchQuery}
              onSearchQueryChange={setSearchQuery}
              onRefresh={handleSync}
              onSelectWidget={setSelectedWidgetId}
            />
            <ResizableHandle withHandle />
            {selectedWidget ? (
              <ResizablePanel defaultSize={65} minSize={30}>
                <div className="h-full flex flex-col">
                  {/* Header with sync button */}
                  <div className="border-b border-border px-6 py-4 flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-semibold">
                        {selectedWidget.displayName}
                      </h2>
                      <code className="text-xs text-muted-foreground font-mono">
                        {selectedWidget.slug}
                      </code>
                    </div>
                    <Button
                      onClick={handleSync}
                      variant="outline"
                      size="sm"
                      disabled={syncing}
                    >
                      <RefreshCw
                        className={`h-4 w-4 mr-2 ${syncing ? "animate-spin" : ""}`}
                      />
                      Sync from MCP
                    </Button>
                  </div>

                  {/* Details and preview */}
                  <div className="flex-1 overflow-auto p-6">
                    <div className="space-y-6">
                      {/* Widget details */}
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs font-medium text-muted-foreground">
                            Display Name
                          </label>
                          <p className="text-sm mt-1">
                            {selectedWidget.displayName}
                          </p>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-muted-foreground">
                            Slug
                          </label>
                          <p className="text-sm mt-1 font-mono">
                            {selectedWidget.slug}
                          </p>
                        </div>
                        {selectedWidget.description && (
                          <div>
                            <label className="text-xs font-medium text-muted-foreground">
                              Description
                            </label>
                            <p className="text-sm mt-1">
                              {selectedWidget.description}
                            </p>
                          </div>
                        )}
                        {selectedWidget.widgetKind && (
                          <div>
                            <label className="text-xs font-medium text-muted-foreground">
                              Widget Kind
                            </label>
                            <p className="text-sm mt-1">
                              {selectedWidget.widgetKind}
                            </p>
                          </div>
                        )}
                        <div>
                          <label className="text-xs font-medium text-muted-foreground">
                            Canonical Resource ID
                          </label>
                          <p className="text-sm mt-1 font-mono text-muted-foreground">
                            {selectedWidget.canonicalResourceId}
                          </p>
                        </div>
                      </div>

                      {/* Widget preview */}
                      <div className="border-t pt-6">
                        <h3 className="text-sm font-semibold mb-4">Preview</h3>
                        <WidgetPreview canonicalWidgetId={selectedWidget._id} />
                      </div>
                    </div>
                  </div>
                </div>
              </ResizablePanel>
            ) : (
              <ResizablePanel defaultSize={65} minSize={30}>
                <div className="h-full flex items-center justify-center">
                  <EmptyState
                    icon={Layout}
                    title="No Widget Selected"
                    description="Select a widget from the list to view its details and preview."
                  />
                </div>
              </ResizablePanel>
            )}
          </ResizablePanelGroup>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
}
